import React, {useRef} from 'react';
import {useTranslation} from "react-i18next";

import {useCreateStationPhoto, useDeleteStationLink, useDeleteStationPhoto} from "../../query";
import useDialog from "../../../../components/useDialog";
import {useFeedbackTools} from "../../../../components/FeedbackTools";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import ImageListItem from "@mui/material/ImageListItem";
import ImageList from "@mui/material/ImageList";
import ButtonBase from "@mui/material/ButtonBase";
import Grid from "@mui/material/Grid";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import {styled} from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

import MDTypography from "materialDashboard/components/MDTypography";
import MDBox from "materialDashboard/components/MDBox";
import MDButton from "materialDashboard/components/MDButton";
import CreateLinkDialog from "./CreateLinkDialog";



const CardMedia = ({station}) => {
    const {t} = useTranslation(['stations']);
    const createLinkDialog = useDialog();
    const {askConfirmation} = useFeedbackTools();

    const deleteStationPhoto = useDeleteStationPhoto(station.id);
    const deleteStationLink = useDeleteStationLink(station.id);

    const handleDeletePhoto = (photoId) => {
        deleteStationPhoto.mutate(photoId);
    }

    const handleOpenLink = (link) => {
        window.open(link, '_blank');
    }

    const handleDeleteStationLink = async (linkId) => {
        const innerDeleteGroup = async () => {
            await deleteStationLink.mutateAsync(linkId);
        }
        await askConfirmation({
            title: t('station.cardMedia.deleteLinkConfirmation'),
            description: t('station.cardMedia.deleteLinkDescription'),
            confirmationText: t('station.cardMedia.deleteLinkConfirmationText'),
            onConfirm: innerDeleteGroup
        });
    }

    return <Grid container columnSpacing={4} rowSpacing={4}>
        <Grid item xs={12} lg={6}>
            <Card>
                <MDBox py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                        {t("station.cardMedia.images")}
                    </MDTypography>
                    {
                        <ImageList sx={{ width: "100%", height: 450, pt: 2 }} cols={3} rowHeight={200}>
                            <ImageAdder station={station}/>
                            {station.photos.filter(p => !!p?.file?.thumbnail?.path).map((photo) => (
                                <ImageListItem key={photo.file.thumbnail.path}>
                                    <img src={photo.file.thumbnail.path}
                                         alt={photo.filename}
                                         loading="lazy"
                                    />
                                    <ImageListItemBar
                                        actionIcon={
                                            <IconButton sx={{ color: 'rgba(255, 255, 255, 0.54)' }} onClick={() => handleDeletePhoto(photo.id)}>
                                                <Icon>delete</Icon>
                                            </IconButton>
                                        }
                                    />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    }
                </MDBox>
            </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
            <Card>
                <MDBox py={2} px={2}>
                    <MDTypography variant="h5" fontWeight="medium" textTransform="capitalize">
                        {t("station.cardMedia.links")}
                    </MDTypography>
                    <MDBox minHeight="10rem" sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "1rem"
                    }}>
                        {
                            station.links.length === 0 &&
                            <>
                                <Icon fontSize="large">inbox</Icon>
                                <MDTypography variant="body2" color="secondary">
                                    {t("station.cardMedia.noLinks")}
                                </MDTypography>
                            </>
                        }
                            {
                                station.links.map((link) => (
                                    <ListItem key={link.id}
                                              secondaryAction={
                                                <Stack direction="row">
                                                    <IconButton size="small" edge="end" aria-label="delete" onClick={() => handleOpenLink(link.link)}>
                                                        <Icon>open_in_new</Icon>
                                                    </IconButton>
                                                    <IconButton size="small" edge="end" aria-label="delete" onClick={() => handleDeleteStationLink(link.id)}>
                                                        <Icon>delete</Icon>
                                                    </IconButton>
                                                </Stack>
                                              }>
                                        <ListItemText>
                                            <Stack>
                                                <MDTypography variant="h6">{link.title}</MDTypography>
                                                <MDTypography variant="body2">{link.description}</MDTypography>
                                                <MDTypography variant="caption">{link.link}</MDTypography>
                                            </Stack>
                                        </ListItemText>
                                    </ListItem>
                                ))
                            }
                            <Divider sx={{width: "100%"}}/>

                            <MDButton variant="gradient"
                                      color="info"
                                      startIcon={<Icon>add</Icon>}
                                      onClick={() => createLinkDialog.open()}>
                                {t("station.cardMedia.addLink")}
                            </MDButton>
                    </MDBox>
                </MDBox>
            </Card>
        </Grid>

        {
            createLinkDialog.isOpen &&
            <CreateLinkDialog open={createLinkDialog.isOpen}
                              onClose={createLinkDialog.close}
                              stationId={station.id}/>
        }
    </Grid>
}

const NewImageButton = styled(ButtonBase)(({ theme }) => ({
    position: 'relative',
    height: 200,
    backgroundColor: theme.palette.common.black,
    opacity: 0.5,
    transition: theme.transitions.create('opacity'),
    color: "white",

    '&:hover': {
        zIndex: 1,
        opacity: 0.7,
    },
}));


const ImageAdder = ({station}) => {
    const inputFile = useRef(null);
    const createStationPhoto = useCreateStationPhoto(station.id);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        createStationPhoto.mutate(file);
        e.target.value = null;
    }

    return <ImageListItem>
        {
            createStationPhoto.isLoading ?
            "Loading..."
            :
            <NewImageButton onClick={() => inputFile.current.click()}>
                <Icon fontSize="large">upload</Icon>
            </NewImageButton>
        }
        <input type='file' id='file'
               ref={inputFile}
               style={{display: 'none'}}
               onChange={handleFileChange}/>
    </ImageListItem>
}

export default CardMedia;